/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 앱 렌더러
 * 버전 : 1.0
 * 파일명 : AppImage.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-12-18
 * ================================================================ 
 * ================================================================ */

import React, { forwardRef, type SVGProps } from "react";
import { ReactComponent as Icon_DownLoad24_Gray } from "./Icon_DownLoad24_Gray.svg";
import { ReactComponent as Alert_Circle } from "./Alert_Circle.svg";
import { ReactComponent as Icon_Phone } from "./Icon_Phone.svg";
import { ReactComponent as Icon_Global } from "./Icon_Global.svg";
import { ReactComponent as Icon_NotePad_Mint } from "./Icon_NotePad_Mint.svg";
import { ReactComponent as Icon_Plus } from "./Icon_Plus.svg";
import { ReactComponent as Icon_Calendar_Grey } from "./Icon_Calendar_Grey.svg";
import { ReactComponent as Icon_Position } from "./Icon_Position.svg";
import { ReactComponent as Icon_Link } from "./Icon_Link.svg";
import { ReactComponent as Icon_Trash } from "./Icon_Trash.svg";
import { ReactComponent as Icon_NotePad_Black } from "./Icon_NotePad_Black.svg";
import { ReactComponent as Graduate_Hat_Gray } from "./Graduate_Hat_Gray.svg";
import { ReactComponent as Icon_School } from "./Icon_School.svg";

const ICONS = {
    IconDownLoad24Gray: Icon_DownLoad24_Gray,
    AlertCircle: Alert_Circle,
    IconPhone: Icon_Phone,
    IconGlobal: Icon_Global,
    IconNotePadMint: Icon_NotePad_Mint,
    IconPlus: Icon_Plus,
    IconCalendarGrey : Icon_Calendar_Grey,
    IconPosition : Icon_Position,
    IconLink : Icon_Link,
    IconTrash : Icon_Trash,
    IconNotePadBlack : Icon_NotePad_Black,
    GraduateHatGray : Graduate_Hat_Gray,
    IconSchool : Icon_School
} as const;

export type IconName = keyof typeof ICONS;

type Props = {
    name: IconName;
    size?: number | string; // width/height 한 번에
    decorative?: boolean;
    title?: string;
    customStyle?: React.CSSProperties;
    fill?: string;
    color?: string; // currentColor 기반 색상 변경용
} & Omit<SVGProps<SVGSVGElement>, "color">;

export const AppImage = forwardRef<SVGSVGElement, Props>(function AppImage(
    { name, size, decorative = false, title, customStyle, fill, color, style, ...rest },
    ref
    ) {
    const Svg = ICONS[name];

    const aria = decorative
        ? { role: "img", "aria-hidden": true as const }
        : { role: "img", "aria-label": title ?? name };

    const mergedStyle: React.CSSProperties = {
        ...(size != null ? { width: size, height: size } : null),
        color: color ?? "currentColor",
        flexShrink: 0,
        ...style,
        ...customStyle,
    };

    return (
        <Svg
        ref={ref}
        {...aria}
        {...rest}
        fill={fill ?? "none"}
        style={mergedStyle}
        />
    );
});
